/**
 * AnnouncementAdmin · Admin blast page for the back-to-school announcement.
 * Route: /academy/admin/announcement
 *
 * Flow:
 *   1. Show recipient list pulled from completed orientations
 *   2. Live email preview (rendered in an iframe for accurate HTML)
 *   3. "Dry Run" or "Send For Real" — dry run logs recipients, real sends via Resend
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import { Mail, Send, Loader2, Eye, Users, TestTube2, AlertTriangle } from 'lucide-react';

const authHeaders = () => {
  // Match the rest of the app — token is stored under `academy_token`.
  const t = localStorage.getItem('academy_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AnnouncementAdmin() {
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState({}); // email -> bool
  const [preview, setPreview] = useState(null); // { subject, html }
  const [activeRecipient, setActiveRecipient] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [testEmail, setTestEmail] = useState('brandon@prsvr.com');
  // Manual recipients: one per line "email | First | Last | family_key | Student1, Student2"
  const [manualText, setManualText] = useState('semira@cohans.com | Semira | Knight | knight | Shane Knight');

  const parseManualRecipients = () => {
    const lines = manualText.split('\n').map(l => l.trim()).filter(Boolean);
    return lines.map(line => {
      const parts = line.split('|').map(p => p.trim());
      const [email, first_name, last_name, family_key, students] = parts;
      return {
        email: email || '',
        first_name: first_name || 'Scholar Family',
        last_name: last_name || '',
        family_key: family_key || '',
        students: (students || '').split(',').map(s => s.trim()).filter(Boolean),
      };
    }).filter(x => x.email && x.email.includes('@'));
  };

  useEffect(() => { (async () => { await loadRecipients(); })(); }, []);

  const loadRecipients = async () => {
    setLoading(true);
    try {
      const r = await api.get('/academy/admin/announcement/recipients', { headers: authHeaders() });
      setRecipients(r.data.recipients || []);
      // default: select all
      const sel = {};
      (r.data.recipients || []).forEach(x => { sel[x.parent_email] = true; });
      setSelected(sel);
      if ((r.data.recipients || []).length > 0) {
        renderPreview(r.data.recipients[0]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Admin login required.');
    } finally { setLoading(false); }
  };

  const renderPreview = async (rcpt) => {
    try {
      const r = await api.post('/academy/admin/announcement/preview', {
        parent_first_name: rcpt?.parent_first_name || 'Scholar Family',
        parent_email: rcpt?.parent_email || null,
        student_names: rcpt?.student_names || [],
      }, { headers: authHeaders() });
      setPreview(r.data);
      setActiveRecipient(rcpt);
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const selectedEmails = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected]
  );

  const toggleAll = (v) => {
    const next = {};
    recipients.forEach(r => { next[r.parent_email] = v; });
    setSelected(next);
  };

  const blast = async (dryRun) => {
    const extras = parseManualRecipients();
    if (selectedEmails.length === 0 && extras.length === 0) { toast.error('No recipients selected.'); return; }
    const totalCount = selectedEmails.length + extras.length;
    if (!dryRun && !window.confirm(`Send the announcement email to ${totalCount} families (${selectedEmails.length} auto + ${extras.length} manual)? This is REAL.`)) return;
    setSending(true);
    try {
      const r = await api.post('/academy/admin/announcement/send', {
        dry_run: dryRun,
        limit_to_emails: selectedEmails,
        extra_recipients: extras,
      }, { headers: authHeaders() });
      setLastResult(r.data);
      toast.success(dryRun
        ? `Dry run OK — would have sent to ${r.data.sent_count}`
        : `Sent to ${r.data.sent_count} (${r.data.skipped_count} skipped)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const testSend = async () => {
    if (!testEmail.trim()) { toast.error('Enter a test email.'); return; }
    setSending(true);
    try {
      const r = await api.post('/academy/admin/announcement/send', {
        test_send_to: testEmail.trim(),
      }, { headers: authHeaders() });
      setLastResult(r.data);
      toast.success(r.data.ok ? `Test email sent to ${testEmail}` : `Test send FAILED — check Resend key`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="announcement-admin-page">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-xl bg-slate-900 text-white p-6 shadow-lg">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-400">
            <Mail className="w-3.5 h-3.5" /> Admin · Announcement Blast
          </div>
          <h1 className="mt-2 text-2xl font-black">Back-to-School Announcement</h1>
          <p className="mt-2 text-slate-300 text-sm">
            Preview the email per family, then dry-run or send to all completed-orientation families.
            Singletary sends will CC <code className="text-amber-300">Jaron.r.singletary@gmail.com</code> for Myles &amp; Gracen.
          </p>
        </div>

        {/* Toolbar */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="text-sm text-slate-700 font-semibold inline-flex items-center gap-2">
            <Users className="w-4 h-4" /> {selectedEmails.length} of {recipients.length} selected
          </div>
          <button onClick={() => toggleAll(true)} className="text-xs font-bold text-slate-700 underline">Select all</button>
          <button onClick={() => toggleAll(false)} className="text-xs font-bold text-slate-500 underline">Clear</button>
          <div className="ml-auto flex flex-wrap gap-2 items-center">
            <input
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="test@you.com"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm w-56"
              data-testid="test-email-input"
            />
            <button data-testid="test-send-btn" onClick={testSend} disabled={sending || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-800 px-4 py-2 text-sm font-bold disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Test Send To Me
            </button>
            <button data-testid="dry-run-btn" onClick={() => blast(true)} disabled={sending || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 px-4 py-2 text-sm font-bold disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />} Dry Run
            </button>
            <button data-testid="send-real-btn" onClick={() => blast(false)} disabled={sending || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-black uppercase tracking-wide disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send For Real
            </button>
          </div>
        </div>

        {/* Manual recipients */}
        <div className="mt-5 rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-black text-slate-500">
            <Mail className="w-3.5 h-3.5" /> Manual Recipients (One Per Line)
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Format: <code>email | First | Last | family_key | Student1, Student2</code> — use for families not yet in orientation.
            (family_key drives auto-CC and tuition; leave blank if unsure.)
          </div>
          <textarea
            data-testid="manual-recipients-input"
            rows={3}
            value={manualText}
            onChange={e => setManualText(e.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono text-slate-800"
            placeholder="semira@cohans.com | Semira | Knight | knight | Shane Knight"
          />
          <div className="mt-1 text-[11px] text-emerald-700 font-semibold">
            {parseManualRecipients().length > 0
              ? `${parseManualRecipients().length} manual recipient(s) will be added when you dry-run or send.`
              : 'No manual recipients yet.'}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Recipient list */}
          <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-black text-slate-500">
              <Users className="w-3.5 h-3.5" /> Recipients
            </div>
            {loading && <div className="mt-3 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
            {!loading && recipients.length === 0 && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 inline-flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <span>No completed orientation submissions found on this environment. Deploy to production for the real list.</span>
              </div>
            )}
            <div className="mt-3 divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
              {recipients.map((r, i) => (
                <label key={r.parent_email} className={`flex items-start gap-2 py-2.5 cursor-pointer ${activeRecipient?.parent_email === r.parent_email ? 'bg-blue-50' : ''}`}>
                  <input type="checkbox" checked={!!selected[r.parent_email]}
                         onChange={e => setSelected({ ...selected, [r.parent_email]: e.target.checked })}
                         data-testid={`recipient-check-${i}`}
                         className="mt-1" />
                  <button onClick={() => renderPreview(r)} className="text-left flex-1 min-w-0" data-testid={`recipient-preview-${i}`}>
                    <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-2">
                      <span>{r.parent_first_name} {r.parent_last_name || ''}</span>
                      {r.status && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider ${
                          r.status === 'complete' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>{r.status.replace('_','-')}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{r.parent_email}</div>
                    {(r.student_names || []).length > 0 && (
                      <div className="text-[11px] text-slate-400 mt-0.5 truncate">Students: {r.student_names.join(', ')}</div>
                    )}
                    {(r.cc || []).length > 0 && (
                      <div className="text-[11px] text-amber-600 font-semibold mt-0.5">CC: {r.cc.join(', ')}</div>
                    )}
                  </button>
                </label>
              ))}
            </div>
          </div>

          {/* Preview iframe */}
          <div className="lg:col-span-2 rounded-xl bg-white border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-black text-slate-500">
                <Eye className="w-3.5 h-3.5" /> Live Preview {activeRecipient ? `· ${activeRecipient.parent_first_name}` : ''}
              </div>
              {preview && <div className="text-xs text-slate-500">Subject: <span className="font-semibold text-slate-700">{preview.subject}</span></div>}
            </div>
            <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
              {preview ? (
                <iframe title="email-preview" srcDoc={preview.html} className="w-full h-[640px] bg-white" data-testid="announcement-preview-iframe" />
              ) : (
                <div className="h-[640px] flex items-center justify-center text-slate-400 text-sm">Select a recipient to render preview</div>
              )}
            </div>
          </div>
        </div>

        {/* Last result */}
        {lastResult && (
          <div className="mt-5 rounded-xl bg-white border border-slate-200 p-4 shadow-sm" data-testid="announcement-last-result">
            <div className="text-xs uppercase tracking-[0.25em] font-black text-slate-500">
              Last {lastResult.dry_run ? 'Dry Run' : 'Send'} Result
            </div>
            <div className="mt-2 text-sm text-slate-700">
              Sent: <strong>{lastResult.sent_count}</strong> · Skipped: <strong>{lastResult.skipped_count}</strong>
            </div>
            <pre className="mt-2 text-[11px] bg-slate-900 text-emerald-200 rounded-lg p-3 max-h-64 overflow-auto">
{JSON.stringify(lastResult, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
